import { formatSubjectType } from '@/utils/buildActivityDetails';

/**
 * Create human-readable activity summary strings
 * Used at render time to build sentences from structured activity fields
 * (summaries are no longer stored on the Activity table)
 * 
 * Examples:
 * - "John Smith created the Event 'Wednesday Service'"
 * - "Jane Doe deleted the Task 'Update website'"
 * - "Bob Wilson assigned Sarah Miller to 'Kids Check-in'"
 */

/**
 * Article helper: returns "a" or "an" based on word
 * @param {string} word
 * @returns {string}
 */
const getArticle = (word) => {
    if (!word) return 'a';
    const firstLetter = word.charAt(0).toLowerCase();
    return ['a', 'e', 'i', 'o', 'u'].includes(firstLetter) ? 'an' : 'a';
};

/**
 * Format entity name with article for natural reading
 * @param {string} subjectType - e.g. 'Event', 'Task', 'Person'
 * @param {string} subjectName - display name
 * @returns {string} - e.g. "the Event 'Wednesday Service'" or "Person 'John Smith'"
 */
const formatEntity = (subjectType, subjectName) => {
    const displayType = formatSubjectType(subjectType);
    // Use "the" for named entities, "a/an" for generic references
    if (subjectName && subjectName !== 'New Event' && subjectName !== 'Untitled') {
        return `the ${displayType} '${subjectName}'`;
    }
    return `${getArticle(displayType)} ${displayType}`;
};

/**
 * Templates for each verb
 */
const templates = {
    create: (actorName, subjectType, subjectName) => 
        `${actorName} created ${formatEntity(subjectType, subjectName)}`,
    
    update: (actorName, subjectType, subjectName) => 
        `${actorName} updated ${formatEntity(subjectType, subjectName)}`,
    
    delete: (actorName, subjectType, subjectName) => 
        `${actorName} deleted ${formatEntity(subjectType, subjectName)}`,
    
    assign: (actorName, subjectType, subjectName, context = {}) => {
        const { targetName } = context;
        if (targetName) {
            return `${actorName} assigned ${targetName} to ${formatEntity(subjectType, subjectName)}`;
        }
        return `${actorName} assigned ${getArticle(subjectType)} ${subjectType}`;
    },
    
    unassign: (actorName, subjectType, subjectName, context = {}) => {
        const { targetName } = context;
        if (targetName) {
            return `${actorName} unassigned ${targetName} from ${formatEntity(subjectType, subjectName)}`;
        }
        return `${actorName} unassigned from ${formatEntity(subjectType, subjectName)}`;
    },

    publish: (actorName, subjectType, subjectName) => 
        `${actorName} published ${formatEntity(subjectType, subjectName)}`,

    close: (actorName, subjectType, subjectName) => 
        `${actorName} closed ${formatEntity(subjectType, subjectName)}`,

    reopen: (actorName, subjectType, subjectName) => 
        `${actorName} reopened ${formatEntity(subjectType, subjectName)}`,

    archive: (actorName, subjectType, subjectName) => 
        `${actorName} archived ${formatEntity(subjectType, subjectName)}`,

    unarchive: (actorName, subjectType, subjectName) => 
        `${actorName} unarchived ${formatEntity(subjectType, subjectName)}`,
};

/**
 * Create a human-readable activity summary
 * 
 * @param {Object} params
 * @param {string} params.actorName - Full name of person performing action
 * @param {string} params.verb - 'create'|'update'|'delete'|'assign'|'unassign'|'publish'|'close'|'reopen'|'archive'|'unarchive'
 * @param {string} params.subjectType - Entity type being acted upon
 * @param {string} params.subjectName - Display name of entity
 * @param {Object} params.context - Optional additional context (targetName for assign/unassign)
 * @returns {string} Human-readable summary sentence
 */
export const createActivitySummary = ({
    actorName,
    verb,
    subjectType,
    subjectName,
    context,
}) => {
    const template = templates[verb];
    if (!template) {
        // Fallback for unknown verbs
        return `${actorName} performed ${verb} on ${formatEntity(subjectType, subjectName)}`;
    }
    return template(actorName, subjectType, subjectName, context);
};

/**
 * Get icon name for verb
 * @param {string} verb
 * @returns {string}
 */
export const getVerbIcon = (verb) => {
    const icons = {
        create: 'add-circle',
        update: 'pencil',
        delete: 'trash',
        assign: 'person-add',
        unassign: 'person-remove',
        publish: 'rocket',
        close: 'lock-closed',
        reopen: 'lock-open',
        archive: 'archive',
        unarchive: 'arrow-undo',
    };
    return icons[verb] || 'activity';
};

/**
 * Get color for subject type (for accent/background)
 * Maps to module-specific colors from themes.js
 * @param {string} subjectType
 * @returns {string}
 */
export const getSubjectTypeColor = (subjectType) => {
    const colors = {
        Person: 'people',
        Event: 'events',
        EventInstance: 'events',
        Task: 'tasks',
        SmallGroup: 'groups',
        Path: 'paths',
        Family: 'people',
        Tag: 'admin',
        Note: 'admin',
        Contribution: 'giving',
        Batch: 'giving',
        Method: 'giving',
        Fund: 'giving',
        Training: 'training',
        TrainingModule: 'training',
        BibleStudy: 'groups',
        SmallGroupType: 'groups',
        SmallGroupSession: 'groups',
        VolunteerTeam: 'tasks',
        Role: 'admin',
        CustomField: 'admin',
        Calendar: 'admin',
        Location: 'admin',
        Address: 'admin',
        QuickFilter: 'admin',
        Registration: 'events',
        Form: 'forms',
    };
    return colors[subjectType] || 'admin';
};
